import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Tag, Avatar, ButtonMenu, Typography } from "@ann/web-components";
import { useFactsContext, useMyMemberContext } from "../../../context";
import { useDebounce } from "../../../../../../../../hooks";
import { UserCellContainer, UserAvatarContainer, UserTextContainer, FactNameLink } from "../Facts.styled";
import dayjs from "dayjs";

// Helpers fuera del hook (no necesitan ser callbacks)
const formatDate = (dateString) => {
  if (!dateString) return "—";
  return dayjs(dateString).format("MMM D, YYYY");
};

const getStatusColor = (status) => {
  const normalizedStatus = typeof status === 'string' ? status.toLowerCase() : '';
  switch (normalizedStatus) {
    case 'active': return 'success';
    case 'pending':return 'yellow';
    case 'pending approval': return 'yellow';
    case 'expired': return 'error';
    case 'draft': return 'yellow';
    default: return 'neutral';
  }
};

const getExpirationColor = (expirationPolicy) => {
  if (!expirationPolicy || expirationPolicy.no_expire || expirationPolicy.noExpire) return 'neutral';
  const limit = expirationPolicy.limit;
  const type = expirationPolicy.type;
  if (limit <= 7 && type === 'DAYS') return 'orange';
  if (limit <= 60 && type === 'DAYS') return 'error';
  if (limit <= 60 && type === 'DAYS') return 'informative';
  return 'neutral';
};

const formatExpiration = (expirationPolicy) => {
  if (!expirationPolicy) return "—";
  if (expirationPolicy.no_expire || expirationPolicy.noExpire) return "No expiration";
  const limit = expirationPolicy.limit;
  const type = expirationPolicy.type;
  if (limit && type) return `${limit}${type === 'DAYS' ? 'd' : type.toLowerCase()}`;
  return "—";
};

const getAvatarUrl = (userName, photoUrl) => {
  if (photoUrl) return photoUrl;
  if (!userName || userName === "—") return null;
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(userName)}&background=2173FF&color=fff`;
};

export const useFacts = ({ onSuggestUpdate } = {}) => {
  // ============================================================================
  // External Hooks
  // ============================================================================
  const { knowledgeId } = useParams();
  const {
    baseFacts,
    baseMeta,
    baseKnowledgebaseUuid,
    facts,
    factsMeta,
    loading,
    error,
    fetchFacts,
    resetFactsToBase,
    resetFacts,
  } = useFactsContext();
  const navigate = useNavigate();
  const { myRole } = useMyMemberContext();

  // ============================================================================
  // State - Filter States
  // ============================================================================
  const [searchValue, setSearchValue] = useState("");
  const [statusFilter, setStatusFilter] = useState(null);
  const [expirationFilter, setExpirationFilter] = useState(null);
  const [sourceFilter, setSourceFilter] = useState(null);

  // ============================================================================
  // Debounced Search Value
  // ============================================================================
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const isSearching = searchValue !== debouncedSearchValue;

  // ============================================================================
  // State - Pagination & Sorting
  // ============================================================================
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState('updated_at');
  const [orderDirection, setOrderDirection] = useState('DESC');

  // ============================================================================
  // State - UI State
  // ============================================================================
  const [isTrainingModalOpen, setIsTrainingModalOpen] = useState(false);
  const [searchKey, setSearchKey] = useState(0);
  const [filtersKey, setFiltersKey] = useState(0);

  // ============================================================================
  // State - Internal State
  // ============================================================================
  const [hasLoadedBase, setHasLoadedBase] = useState(false);
  const prevKnowledgeIdRef = useRef(null);
  const prevSanitizedSearchRef = useRef(null);

  // ============================================================================
  // useMemo - Derived Data (for API calls)
  // ============================================================================
  const filtersPayload = useMemo(() => {
    const payload = {};

    if (statusFilter) {
      payload.status = statusFilter;
    }

    if (expirationFilter) {
      payload.expiration = expirationFilter;
    }

    if (sourceFilter) {
      payload.source_type = sourceFilter;
    }

    return Object.keys(payload).length ? payload : undefined;
  }, [statusFilter, expirationFilter, sourceFilter]);

  const sanitizedSearch = useMemo(() => {
    const trimmed = debouncedSearchValue.trim();
    return trimmed.length ? trimmed : null;
  }, [debouncedSearchValue]);

  // ============================================================================
  // useMemo - Check if there are active filters
  // ============================================================================
  const hasActiveFilters = useMemo(() => {
    return Boolean(
      sanitizedSearch ||
      statusFilter ||
      expirationFilter ||
      sourceFilter
    );
  }, [sanitizedSearch, statusFilter, expirationFilter, sourceFilter]);

  // ============================================================================
  // Utility Callbacks
  // ============================================================================
  const resetLocalState = useCallback(() => {
    setSearchValue("");
    setStatusFilter(null);
    setExpirationFilter(null);
    setSourceFilter(null);
    setPage(1);
    setPageSize(20);
    setOrderBy('updated_at');
    setOrderDirection('DESC');
  }, []);

  // ============================================================================
  // useEffect Hooks
  // ============================================================================
  useEffect(() => {
    if (!knowledgeId) return;

    // Validate baseKnowledgebaseUuid mismatch
    // if (baseKnowledgebaseUuid && baseKnowledgebaseUuid !== knowledgeId) {
    //   resetFacts();
    // }

    // Validate prevKnowledgeIdRef mismatch
    if (prevKnowledgeIdRef.current && prevKnowledgeIdRef.current !== knowledgeId) {
      resetFacts();
    }

    prevKnowledgeIdRef.current = knowledgeId;
    setHasLoadedBase(false);
    resetLocalState();
  }, [knowledgeId, resetFacts, resetLocalState]);

  useEffect(() => {
    if (!knowledgeId) return;

    const hasFilters = Boolean(filtersPayload || sanitizedSearch);
    const hasSorting = orderBy !== 'updated_at' || orderDirection !== 'DESC';
    const hasPaginationChanges = page !== 1 || pageSize !== 20;

    // Detectar cuando el search cambia de tener valor a ser null
    const searchWasCleared = 
      prevSanitizedSearchRef.current !== null && 
      sanitizedSearch === null;

    const params = {
      knowledgebase_uuid: knowledgeId,
      page,
      page_size: pageSize,
      order_by: orderBy,
      order_direction: orderDirection,
      filters: filtersPayload,
      search: sanitizedSearch ? { keyword: sanitizedSearch } : undefined,
    };

    if (!hasFilters && !hasSorting && !hasPaginationChanges) {
      if (!hasLoadedBase) {
        if (baseFacts.length) {
          resetFactsToBase();
          setHasLoadedBase(true);
          prevSanitizedSearchRef.current = sanitizedSearch;
          return;
        }

        fetchFacts(params, { persistAsBase: true });
        setHasLoadedBase(true);
        prevSanitizedSearchRef.current = sanitizedSearch;
        return;
      }

      // Si el search fue borrado y no hay otros filtros, resetear a base
      if (searchWasCleared && !filtersPayload) {
        resetFactsToBase();
        prevSanitizedSearchRef.current = sanitizedSearch;
        return;
      }

      prevSanitizedSearchRef.current = sanitizedSearch;
      return;
    }

    fetchFacts(params, { persistAsBase: false });
    prevSanitizedSearchRef.current = sanitizedSearch;
  }, [
    knowledgeId,
    page,
    pageSize,
    orderBy,
    orderDirection,
    filtersPayload,
    sanitizedSearch,
    fetchFacts,
    hasLoadedBase,
    baseFacts.length,
    resetFactsToBase,
  ]);

  // ============================================================================
  // Handlers - Filter Handlers
  // ============================================================================
  const handleStatusChange = useCallback((status) => {
    setStatusFilter(status || null);
    setPage(1);
  }, []);

  const handleExpirationChange = useCallback((expiration) => {
    setExpirationFilter(expiration || null);
    setPage(1);
  }, []);

  const handleSourceChange = useCallback((source) => {
    setSourceFilter(source || null);
    setPage(1);
  }, []);

  // ============================================================================
  // Handlers - Search Handlers
  // ============================================================================
  const handleSearchChange = useCallback((value) => {
    setSearchValue(value);
  }, []);

  // ============================================================================
  // Handlers - Clear Handlers
  // ============================================================================
  const handleClearAllFilters = useCallback(() => {
    setSearchValue("");
    setStatusFilter(null);
    setExpirationFilter(null);
    setSourceFilter(null);
    setPage(1);
    setSearchKey((prev) => prev + 1);
    setFiltersKey((prev) => prev + 1);
    resetFactsToBase();
  }, [resetFactsToBase]);

  const handleClearAllFiltersWithKeys = useCallback(() => {
    handleClearAllFilters();
  }, [handleClearAllFilters]);

  // ============================================================================
  // Handlers - Pagination Handlers
  // ============================================================================
  const handlePageChange = useCallback(
    (nextPage) => {
      setPage(nextPage);

      if (nextPage === 1) {
        const hasFilters = Boolean(
          statusFilter ||
          expirationFilter ||
          sourceFilter ||
          sanitizedSearch
        );
        const isDefaultSorting = orderBy === 'updated_at' && orderDirection === 'DESC';

        if (!hasFilters && isDefaultSorting) {
          resetFactsToBase();
        }
      }
    },
    [
      statusFilter,
      expirationFilter,
      sourceFilter,
      sanitizedSearch,
      orderBy,
      orderDirection,
      resetFactsToBase,
    ]
  );

  const handleItemsPerPageChange = useCallback(
    (itemsPerPage) => {
      setPageSize(itemsPerPage);
      setPage(1);

      if (itemsPerPage === 20) {
        const hasFilters = Boolean(
          statusFilter ||
          expirationFilter ||
          sourceFilter ||
          sanitizedSearch
        );
        const isDefaultSorting = orderBy === 'updated_at' && orderDirection === 'DESC';

        if (!hasFilters && isDefaultSorting) {
          resetFactsToBase();
        }
      }
    },
    [
      statusFilter,
      expirationFilter,
      sourceFilter,
      sanitizedSearch,
      orderBy,
      orderDirection,
      resetFactsToBase,
    ]
  );

  // ============================================================================
  // Handlers - Sort Handlers
  // ============================================================================
  const handleSortChange = useCallback((selectedOption) => {
    const orderByMap = { 'name': 'title', 'status': 'status', 'updated': 'updated_at' };
    const newOrderBy = orderByMap[selectedOption] || 'updated_at';
    setOrderBy(newOrderBy);
    setPage(1);
  }, []);

  const handleOrderChange = useCallback((currentOrder) => {
    const newOrder = currentOrder === 'asc' ? 'DESC' : 'ASC';
    setOrderDirection(newOrder);
  }, []);

  // ============================================================================
  // Handlers - Action Handlers
  // ============================================================================

  // ============================================================================
  // useMemo - Data Transformations
  // ============================================================================
  const tableData = useMemo(() => {
    // Determinar permisos según el rol
    const canEdit = myRole === 'ADMIN';
    const canDeactivate = myRole === 'ADMIN';
    const canSuggest = myRole === 'ADMIN' || myRole === 'CONTRIBUTOR';

    return facts.map((fact) => {
      // Programación defensiva: camelCase primero, snake_case como fallback
      const factUuid = fact.factUuid || fact.fact_uuid || fact.id;
      const factTitle = fact.title;
      const factDescription = fact.description;
      const factStatus = fact.status;
      const expirationPolicy = fact.expirationPolicy || fact.expiration_policy;
      const sourceType = fact.sourceType || fact.source_type;
      const createdBy = fact.createdBy || fact.created_by;
      const createdByPhoto = fact.createdByPhoto || fact.created_by_photo;
      const verifiedBy = fact.verifiedBy || fact.verified_by;
      const verifiedByPhoto = fact.verifiedByPhoto || fact.verified_by_photo;
      const updatedAt = fact.updatedAt || fact.updated_at;

      const statusText = factStatus || "—";
      const statusDisplay = statusText !== "—"
        ? statusText.charAt(0).toUpperCase() + statusText.slice(1).toLowerCase()
        : "—";

      const handleFactNameClick = (e) => {
        e.stopPropagation();
        navigate(`/v2/knowledge/knowledge-hub/${knowledgeId}/facts/${factUuid}/overview`);
      };

      const handleEditClick = () => {
        navigate(`/v2/knowledge/knowledge-hub/${knowledgeId}/facts/${factUuid}/overview`);
      };

      const createdByAvatar = getAvatarUrl(createdBy, createdByPhoto);
      const verifiedByAvatar = verifiedBy && verifiedBy !== "Not Verified" 
        ? getAvatarUrl(verifiedBy, verifiedByPhoto) 
        : null;

      return {
        id: factUuid,
        factName: factTitle === "—" ? "—" : (
          <FactNameLink onClick={handleFactNameClick}>
            <Typography category="body" size="medium" weight="regular" color="suptitle" maxCharacters={20}>
              {factTitle || "—"}
            </Typography>
          </FactNameLink>
        ),

        factDescription: factDescription === "—" ? "—":(
          <Typography category="body" size="medium" weight="regular" color="body" maxCharacters={30}>
            {factDescription || "—"}
          </Typography>
          
        ),
        status: statusDisplay === "—" ? "—" : (
          <Tag color={getStatusColor(factStatus)} size="medium" text={statusDisplay} />
        ),
        expiration: (
          <Tag
            color={getExpirationColor(expirationPolicy)}
            size="medium"
            text={formatExpiration(expirationPolicy)}
          />
        ),
        source: sourceType || "—",
        createdBy: createdBy === "—" ? "—" : (
          <UserCellContainer>
            {createdByAvatar && (
              <UserAvatarContainer>
                <Avatar size="md" src={createdByAvatar} alt={createdBy} />
              </UserAvatarContainer>
            )}
            <Typography category="body" size="medium" weight="regular" color="body" maxCharacters={20}>
              {createdBy || "—"}
            </Typography>
          </UserCellContainer>
        ),
        verifiedBy: verifiedBy === "Not Verified" ? "Not Verified" : (
          <UserCellContainer>
            {verifiedByAvatar && (
              <UserAvatarContainer>
                <Avatar size="md" src={verifiedByAvatar} alt={verifiedBy} />
              </UserAvatarContainer>
            )}
            <Typography category="body" size="medium" weight="regular" color="body" maxCharacters={20}>
              {verifiedBy || "—"}
            </Typography>
          </UserCellContainer>
        ),
        lastUpdated: formatDate(updatedAt) || "—",
        actions: (
          <ButtonMenu
            customIcon="dotsMenu"
            disableEffects
            menuPosition="bottom"
            size="medium"
            type="tertiary"
            width="180px"
          >
            <ButtonMenu.Menu icon={true}>
              <ButtonMenu.Item
                label="Edit"
                value="edit"
                icon="edit"
                // disabled={!canEdit} // TODO: Descomentar cuando myMember está funcionando
                onClick={handleEditClick}
              >
                Edit
              </ButtonMenu.Item>
              <ButtonMenu.Item
                label="Deactivate"
                value="deactivate"
                icon="delete"
                // disabled={!canDeactivate} // TODO: Descomentar cuando myMember está funcionando
              >
                Deactivate
              </ButtonMenu.Item>
              <ButtonMenu.Item
                label="Suggest Update"
                value="suggestUpdate"
                icon="sms"
                // disabled={!canSuggest} // TODO: Descomentar cuando myMember está funcionando
                onClick={() => onSuggestUpdate?.({
                  fact_uuid: factUuid,
                  title: factTitle,
                })}
              >
                Suggest Update
              </ButtonMenu.Item>
            </ButtonMenu.Menu>
          </ButtonMenu>
        ),
      };
    });
  }, [facts, navigate, knowledgeId, onSuggestUpdate, myRole]);

  const headers = useMemo(() => [
    { label: 'Fact Name', value: 'factName' },
    { label: 'Fact Description', value: 'factDescription' },
    { label: 'Status', value: 'status' },
    { label: 'Expiration', value: 'expiration' },
    { label: 'Source', value: 'source' },
    { label: 'Created By', value: 'createdBy' },
    { label: 'Verified By', value: 'verifiedBy' },
    { label: 'Last Updated', value: 'lastUpdated' },
    { label: 'Actions', value: 'actions' }
  ], []);

  // ============================================================================
  // Computed Values
  // ============================================================================
  const orderByMap = { 'title': 'name', 'status': 'status', 'updated_at': 'updated' };
  const selectedSortOption = orderByMap[orderBy] || 'updated';
  const orderDirectionDisplay = orderDirection?.toLowerCase() || 'desc';

  // ============================================================================
  // Return
  // ============================================================================
  return {
    loading: loading || isSearching,
    tableData,
    headers,
    currentPage: page,
    itemsPerPage: pageSize,
    totalItems: factsMeta?.totalItems || 0,
    totalPages: factsMeta?.totalPages || 0,
    filters: {
      statusOptions: [
        { label: "Active", value: "active" },
        { label: "Pending Approval", value: "pending" },
        { label: "Expired", value: "expired" },
      ],
      expirationOptions: [
        { label: "7 days", value: "7d" },
        { label: "30 days", value: "30d" },
        { label: "60 days", value: "60d" },
        { label: "90 days", value: "90d" },
      ],
      sourceOptions: [
        { label: "Contributor", value: "contributor" },
        { label: "Train Agent", value: "train" },
      ],
      selectedStatus: statusFilter,
      selectedExpiration: expirationFilter,
      selectedSource: sourceFilter,
      searchValue,
      hasActiveFilters,
    },
    filterHandlers: {
      handleSearchChange,
      handleStatusChange,
      handleExpirationChange,
      handleSourceChange,
      handleClearAllFiltersWithKeys,
    },
    filterUI: {
      searchKey,
      filtersKey,
    },
    sorting: {
      selectedSortOption,
      orderDirection: orderDirectionDisplay,
      handleSortChange,
      handleOrderChange,
    },
    handlePageChange,
    handleItemsPerPageChange,
    isTrainingModalOpen,
    setIsTrainingModalOpen,
  };
};
